import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import "./styles.scss";

const isDev = process.env.NODE_ENV === "development";
const BASE_URL = isDev ? "http://localhost:9999" : "https://builder.thrillseekers.com";

export const BikeModelSelectionModal = ({ show, onHide, onSelectBike, isRequiringModelSelectionForCheckout = false }) => {
  const [bikeTypes, setBikeTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [years, setYears] = useState([]);
  const [models, setModels] = useState([]);
  const [bikeTypeBrandMap, setBikeTypeBrandMap] = useState({});
  const [buttonHover, setButtonHover] = useState(false);

  const [selectedBikeType, setSelectedBikeType] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedModel, setSelectedModel] = useState("");

  const [loadingStates, setLoadingStates] = useState({
    bikeTypes: true,
    brands: false,
    years: false,
    models: false,
  });
  const [error, setError] = useState(null);

  const bikeTypesWithoutYears = ["Ebike", "Other"];

  const fetchBikeTypeBrands = async () => {
    try {
      const res = await Promise.race([
        fetch(`/data/bikeTypeBrands.json`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error("Request timed out")), 8000)),
      ]);
      const data = await res.json();
      setBikeTypeBrandMap(data);
      setBikeTypes(Object.keys(data));
      setLoadingStates((prev) => ({ ...prev, bikeTypes: false }));
    } catch (error) {
      console.error("Error fetching bike type brands:", error);
      setError("Failed to load bike types. Please refresh and try again.");
      setLoadingStates((prev) => ({ ...prev, bikeTypes: false }));
    }
  };

  const fetchYears = async (bikeType, brand) => {
    try {
      setLoadingStates((prev) => ({ ...prev, years: true }));
      const url = `${BASE_URL}/.netlify/functions/fetch-shopify-data?bikeType=${bikeType}&brand=${brand}`;
      const res = await Promise.race([
        fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error("Request timed out")), 8000)),
      ]);
      const data = await res.json();
      setYears(data);
      setLoadingStates((prev) => ({ ...prev, years: false }));
    } catch (error) {
      console.error("Error fetching years:", error);
      setError("Failed to load years. Please refresh and try again.");
      setLoadingStates((prev) => ({ ...prev, years: false }));
    }
  };

  const fetchModels = async (bikeType, year, brand) => {
    try {
      setLoadingStates((prev) => ({ ...prev, models: true }));
      const url = `${BASE_URL}/.netlify/functions/fetch-shopify-data?bikeType=${bikeType}&brand=${brand}&year=${year}`;
      const res = await Promise.race([
        fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error("Request timed out")), 8000)),
      ]);
      const data = await res.json();
      setModels(data);
      setLoadingStates((prev) => ({ ...prev, models: false }));
    } catch (error) {
      console.error("Error fetching models:", error);
      setError("Failed to load models. Please refresh and try again.");
      setLoadingStates((prev) => ({ ...prev, models: false }));
    }
  };

  const parseModelFromTitle = (title, year, brand) => {
    return title.replace(year, "").replace(brand, "").trim();
  };

  useEffect(() => {
    if (show) {
      fetchBikeTypeBrands();
    }
  }, [show]);

  useEffect(() => {
    if (selectedBikeType) {
      setError(null);
      setBrands(bikeTypeBrandMap[selectedBikeType] || []);
      setSelectedBrand("");
      setSelectedYear("");
      setSelectedModel("");
    }
  }, [selectedBikeType, bikeTypeBrandMap]);

  useEffect(() => {
    if (selectedBrand) {
      setError(null);
      if (bikeTypesWithoutYears.includes(selectedBikeType)) {
        fetchModels(selectedBikeType, "N/A", selectedBrand);
      } else {
        fetchYears(selectedBikeType, selectedBrand);
      }
      setSelectedYear("");
      setSelectedModel("");
    }
  }, [selectedBrand]);

  useEffect(() => {
    if (selectedYear) {
      setError(null);
      fetchModels(selectedBikeType, selectedYear, selectedBrand);
      setSelectedModel("");
    }
  }, [selectedYear]);

  const handleModelSelect = (model) => {
    onSelectBike(model);
    onHide();
  };

  return (
    <Modal show={show} size="md" centered onHide={onHide}>
      <Modal.Header closeButton closeVariant="white" className="custom-modal-header">
        <Modal.Title>Select your bike model!</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isRequiringModelSelectionForCheckout && !selectedBikeType && (
          <p
            style={{
              color: "red",
              textAlign: "center",
              marginBottom: "10px",
            }}
          >
            Please select your bike model before checking out.
            <br />
            Your configuration will be saved!
          </p>
        )}
        <div
          className="all-selector-row"
          style={{
            paddingTop: isRequiringModelSelectionForCheckout ? "20px" : "0",
            paddingBottom: isRequiringModelSelectionForCheckout ? "20px" : "0",
          }}
        >
          {loadingStates.bikeTypes ? (
            <p className="loader-text">Loading bike types...</p>
          ) : (
            <select
              className={`generic-select ${selectedBikeType ? "selected" : ""}`}
              value={selectedBikeType}
              onChange={(e) => setSelectedBikeType(e.target.value)}
            >
              <option value="" disabled>
                -- Select Bike Type --
              </option>
              {bikeTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          )}

          {selectedBikeType && (
            <select className={`generic-select ${selectedBrand ? "selected" : ""}`} value={selectedBrand} onChange={(e) => setSelectedBrand(e.target.value)}>
              <option value="" disabled>
                -- Select Brand --
              </option>
              {brands.map((brand) => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
            </select>
          )}

          {selectedBrand && !bikeTypesWithoutYears.includes(selectedBikeType) && (
            <>
              {loadingStates.years ? (
                <p className="loader-text">Loading years...</p>
              ) : (
                <select className={`generic-select ${selectedYear ? "selected" : ""}`} value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
                  <option value="" disabled>
                    -- Select Year --
                  </option>
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              )}
            </>
          )}

          {(selectedYear || (bikeTypesWithoutYears.includes(selectedBikeType) && selectedBrand)) && (
            <>
              {loadingStates.models ? (
                <p className="loader-text">Loading models...</p>
              ) : (
                <>
                  <select
                    className={`generic-select ${selectedModel ? "selected" : ""}`}
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                  >
                    <option value="" disabled>
                      -- Select Model --
                    </option>
                    {models.map((model) => (
                      <option key={model.handle} value={model.handle}>
                        {parseModelFromTitle(model.title, selectedYear, selectedBrand)}
                      </option>
                    ))}
                  </select>
                  {selectedModel && (
                    <button
                      onClick={() => handleModelSelect(selectedModel)}
                      style={{
                        backgroundColor: "#fa6b00",
                        color: "white",
                        border: "2px solid #fa6b00",
                        borderRadius: "8px",
                        padding: "8px",
                        fontWeight: "bold",
                        fontSize: "21px",
                        cursor: "pointer",
                        width: "100%",
                        maxWidth: "300px",
                        marginLeft: "auto",
                        marginRight: "auto",
                        display: "block",
                        marginTop: "20px",
                      }}
                    >
                      Customize {parseModelFromTitle(models.find((m) => m.handle === selectedModel)?.title || "", selectedYear, selectedBrand)}
                    </button>
                  )}
                </>
              )}
            </>
          )}

          {error && <p className="error-text">{error}</p>}

          {!isRequiringModelSelectionForCheckout && (
            <>
              <div className="separator">
                <span>or</span>
              </div>
              <button
                onClick={onHide}
                onMouseOver={() => setButtonHover(true)}
                onMouseOut={() => setButtonHover(false)}
                style={{
                  backgroundColor: buttonHover ? "#fa6b00" : "rgba(250, 107, 0, 0.1)",
                  color: buttonHover ? "white" : "#000",
                  border: "2px solid #fa6b00",
                  borderRadius: "8px",
                  padding: "10px 15px",
                  fontWeight: "bold",
                  fontSize: "18px",
                  cursor: "pointer",
                  width: "100%",
                  maxWidth: "300px",
                  marginLeft: "auto",
                  marginRight: "auto",
                  display: "block",
                }}
              >
                Start with demo model
              </button>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};
